import { useState } from "react";
import { NavLink } from "react-router-dom";
import type { ScanResult } from "./api";

/// Anel de pincelada da logo Dépayser.
///
/// Traçado à mão a partir da arte: círculo irregular, aberto num ponto, com
/// espessura variável e as duas pinceladas laterais. É SVG e não a imagem
/// original de propósito — herda a cor de quem usa (preto sobre limão no ícone,
/// limão sobre grafite no cabeçalho) e não perde nitidez em nenhuma tela.
///
/// Para trocar pelo traçado exato da arte, ver README, seção "Identidade
/// visual": basta substituir os `path` daqui, a interface não muda.
export function BrushRing({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 120 120" aria-hidden="true">
      <g fill="none" stroke="currentColor" strokeLinecap="round">
        {/* Volta principal, aberta em cima à direita como no original. */}
        <path
          d="M88 24 C 74 13, 50 11, 34 22 C 14 35, 10 64, 24 82 C 39 101, 71 102, 88 86 C 101 73, 102 52, 92 38"
          strokeWidth="7.5"
        />
        {/* Segunda passada, mais grossa na lateral esquerda — é o que dá o peso
            irregular de pincel em vez de um anel uniforme. */}
        <path d="M33 26 C 16 40, 14 66, 27 81" strokeWidth="11" />
        {/* Fecho curto por dentro, sobrepondo a abertura. */}
        <path d="M86 88 C 96 79, 101 66, 99 54" strokeWidth="5" opacity="0.9" />

        {/* Pinceladas laterais da marca. */}
        <path d="M4 56 L 20 54" strokeWidth="3.5" />
        <path d="M6 64 L 17 63" strokeWidth="2.5" />
        <path d="M100 54 L 116 52" strokeWidth="3.5" />
        <path d="M103 63 L 114 62" strokeWidth="2.5" />
      </g>
    </svg>
  );
}

/// A marca completa: anel de pincelada, wordmark e a faixa "ACESSORIOS".
///
/// Reconstruída em SVG a partir da arte. O anel e a faixa saem bem próximos do
/// original; o wordmark usa uma sans pesada com espaçamento apertado, porque a
/// fonte de pincel da arte não é reproduzível por traçado.
///
/// Para trocar pela imagem original, salve o arquivo em
/// `apps/app/public/logo.png` — o componente passa a usá-lo automaticamente,
/// e cai neste desenho quando ele não existe.
export function BrandLockup({ className }: { className?: string }) {
  const [semArquivo, setSemArquivo] = useState(false);

  // Tenta a arte original primeiro. Se `logo.png` não estiver em public/, o
  // onError troca para o desenho vetorial — sem quebrar a tela e sem exigir
  // alteração de código quando o arquivo aparecer.
  if (!semArquivo) {
    return (
      <img
        className={className}
        src="/logo.png"
        alt="Dépayser Acessórios"
        style={{ width: "100%", height: "auto", borderRadius: 28, display: "block" }}
        onError={() => setSemArquivo(true)}
      />
    );
  }

  return (
    <div className={className} style={{ display: "grid", placeItems: "center" }}>
      <svg viewBox="0 0 500 500" role="img" aria-label="Dépayser Acessórios" width="100%">
        <rect width="500" height="500" rx="28" fill="var(--dep-lime)" />

        <g fill="none" stroke="var(--dep-black)" strokeLinecap="round">
          <path
            d="M382 128 C 344 82, 250 68, 186 108 C 106 158, 96 268, 152 340
               C 210 414, 328 418, 386 356 C 434 304, 436 224, 398 168"
            strokeWidth="16"
          />
          <path d="M180 118 C 108 172, 100 272, 156 336" strokeWidth="23" />
          <path d="M388 352 C 424 316, 442 258, 434 214" strokeWidth="11" opacity="0.92" />

          <path d="M16 232 L 74 226" strokeWidth="8" />
          <path d="M24 258 L 66 254" strokeWidth="6" />
          <path d="M426 226 L 484 220" strokeWidth="8" />
          <path d="M434 254 L 476 250" strokeWidth="6" />
        </g>

        <text
          x="250"
          y="258"
          textAnchor="middle"
          fill="var(--dep-black)"
          fontFamily="var(--font-sans)"
          fontWeight="800"
          fontSize="76"
          letterSpacing="-1"
        >
          DÉPAYSER
        </text>

        {/* Faixa de pincelada sob o wordmark, com as pontas irregulares. */}
        <path
          d="M150 286 L 372 280 L 380 316 L 158 322 Z"
          fill="var(--dep-black)"
        />
        <text
          x="266"
          y="311"
          textAnchor="middle"
          fill="var(--dep-lime)"
          fontFamily="var(--font-sans)"
          fontWeight="700"
          fontSize="27"
          letterSpacing="2.5"
        >
          ACESSORIOS
        </text>
      </svg>
    </div>
  );
}

/// Símbolo de QR code — o ícone do botão de escanear.
///
/// Os três marcadores de canto são o que qualquer pessoa reconhece como "leia
/// um código", e é isso que o botão faz. O anel da marca continua no cabeçalho,
/// onde ele identifica a Dépayser em vez de instruir uma ação.
export function QrIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5z" />
      <path d="M13 3h8v8h-8V3zm2 2v4h4V5h-4z" />
      <path d="M3 13h8v8H3v-8zm2 2v4h4v-4H5z" />
      <path d="M6 6h2v2H6V6zM16 6h2v2h-2V6zM6 16h2v2H6v-2z" />
      <path d="M13 13h3v3h-3v-3zM18 13h3v2h-3v-2zM17 17h4v2h-4v-2zM13 18h2v3h-2v-3zM17 20h4v1h-4v-1z" />
    </svg>
  );
}

const PILL: Record<ScanResult, { className: string; text: string }> = {
  OK: { className: "pill ok", text: "Bipado" },
  DUPLICATE: { className: "pill dup", text: "Repetido" },
  PENDING_ACCOUNT: { className: "pill pending", text: "Falta conta" },
  UNIDENTIFIED: { className: "pill unknown", text: "Não encontrado" },
};

export function ResultPill({ result }: { result: ScanResult }) {
  const { className, text } = PILL[result];
  return <span className={className}>{text}</span>;
}

/// `somenteDevolucao`: o Setor 2 só cuida de devolução — Bipar, Histórico,
/// Retirada e Mercado Livre são trabalho de quem separa o pedido, não dele.
/// Perfil continua porque é dali que se sai da conta.
export function BottomNav({ somenteDevolucao = false }: { somenteDevolucao?: boolean }) {
  if (somenteDevolucao) {
    return (
      <nav className="bottom-nav">
        <NavLink to="/devolucao" end>
          Devolução
        </NavLink>
        <NavLink to="/perfil">Perfil</NavLink>
      </nav>
    );
  }

  return (
    <nav className="bottom-nav">
      <NavLink to="/" end>
        Bipar
      </NavLink>
      <NavLink to="/historico">Histórico</NavLink>
      <NavLink to="/devolucao">Devolução</NavLink>
      <NavLink to="/retirada">Retirada</NavLink>
      <NavLink to="/mercado-livre">
        Mercado<br />Livre
      </NavLink>
      <NavLink to="/perfil">Perfil</NavLink>
    </nav>
  );
}

/// A logo como emblema compacto, para barras e cabeçalhos.
///
/// A arte tem fundo verde limão sólido, então sobre a barra preta ela vira um
/// quadrado. Arredondado e em tamanho pequeno, lê como emblema de marca em vez
/// de imagem colada fora de lugar.
export function BrandBadge({ size = 30 }: { size?: number }) {
  const [semArquivo, setSemArquivo] = useState(false);

  if (semArquivo) return <BrushRing />;

  return (
    <img
      src="/logo.png"
      alt="Dépayser"
      width={size}
      height={size}
      style={{ borderRadius: 7, display: "block", flex: "none", objectFit: "cover" }}
      onError={() => setSemArquivo(true)}
    />
  );
}

export function TopBar({ name, count }: { name: string; count: number }) {
  return (
    <header className="topbar">
      <div className="brand">
        <BrandBadge />
        DÉPAYSER
      </div>
      <div className="operator">
        {name.split(" ")[0]}
        <b>{count}</b>
      </div>
    </header>
  );
}

/// O arquivo do ícone de cada divisão.
///
/// Mora em `apps/app/public/icones/`, que o servidor entrega na raiz — e a
/// raiz é a única pasta que o painel E o aplicativo alcançam com o mesmo
/// caminho. Ficou lá por isso: antes eram duas cópias, uma por aplicação, e
/// duas cópias querem dizer que um dia alguém troca uma logo e esquece a
/// outra.
///
/// O nome é fixo por divisão para que trocar a logo seja trocar o arquivo —
/// sem passar por código, sem deploy.
///
/// "Speed" aponta para o mesmo arquivo da Shopee porque as coletas fechadas
/// antes de 04/09 guardaram a divisão com aquele nome. O histórico não se
/// reescreve; ele só precisa continuar sendo legível.
///
/// A caixa do avulso é desenhada por nós: avulso não tem marca, é venda de
/// WhatsApp e balcão. As outras são as logos das plataformas.
const ICONE: Record<string, string> = {
  ML: "/icones/ml.png",
  Shopee: "/icones/shopee.png",
  Speed: "/icones/shopee.png",
  Avulso: "/icones/avulso.svg",
  "Loja Integrada": "/icones/loja-integrada.png",
};

/// O ícone de uma divisão, na conferência da coleta.
///
/// Cai no NOME quando o arquivo não existe — divisão nova, ou logo que
/// ainda não foi posta. Uma bolinha só com número seria pior que feia:
/// ninguém saberia de qual plataforma é, justamente na hora de conferir
/// com o motorista.
export function BolinhaIcone({ nome }: { nome: string }) {
  const [semIcone, setSemIcone] = useState(false);
  const arquivo = ICONE[nome];

  if (!arquivo || semIcone) return <span className="nome">{nome}</span>;

  return (
    <img src={arquivo} alt={nome} className="icone" onError={() => setSemIcone(true)} />
  );
}
